import {scaleOrdinal} from 'd3-scale';
import {schemeCategory10} from 'd3-scale-chromatic';
import {groupBy, partial} from 'lodash';
import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {
  setDistributionPlotInterpolator,
  setDistributionPlotOptions,
} from './actions';
import DistributionGroup from './DistributionGroup';
import Typography from '@mui/material/Typography';
import {
  DIST_PLOT_OPTIONS,
  DISTRIBUTION_PLOT_INTERPOLATOR_OBJ,
} from './reducers';

function ViolinDistributionPlot(props) {
  const {
    cachedData,
    categoricalNames,
    chartOptions,
    dataset,
    distributionData,
    distributionPlotOptions,
    distributionPlotInterpolator,
    embeddingData,
    globalFeatureSummary,
    onDistributionPlotOptions,
    selectedDistributionData,
  } = props;
  
  // 处理颜色插值器更新
  function onInterpolator(dataType, value) {
    const existingInterpolator = props.distributionPlotInterpolator[dataType];
    value.scale = existingInterpolator.scale;
    props.distributionPlotInterpolator[dataType] = value;
    props.onInterpolator(Object.assign({}, props.distributionPlotInterpolator));
  }

  // 处理颜色缩放更新
  function onColorScalingChange(dataType, value) {
    const existingInterpolator = props.distributionPlotInterpolator[dataType];
    existingInterpolator.scale = value;
    props.onInterpolator(Object.assign({}, props.distributionPlotInterpolator));
  }

  // 处理分布图选项更新，强制设置chartType为violin
  function onDistributionPlotOptionsDataType(dataType, value) {
    // 确保chartType始终为violin
    const newValue = {...value};
    if (newValue.chartType) {
      delete newValue.chartType; // 移除任何可能设置图表类型的尝试
    }
    
    props.distributionPlotOptions[dataType] = Object.assign(
      {},
      props.distributionPlotOptions[dataType],
      newValue,
      {chartType: 'violin'} // 强制设置为violin
    );
    
    onDistributionPlotOptions(Object.assign({}, props.distributionPlotOptions));
  }

  // 确保所有数据类型的图表类型都设置为violin
  useEffect(() => {
    const keys = Object.keys(distributionData);
    let updated = false;
    
    for (const key of keys) {
      if (!distributionPlotOptions[key] || 
          distributionPlotOptions[key].chartType !== 'violin') {
        if (!distributionPlotOptions[key]) {
          distributionPlotOptions[key] = Object.assign({}, DIST_PLOT_OPTIONS);
          distributionPlotInterpolator[key] = Object.assign(
            {},
            DISTRIBUTION_PLOT_INTERPOLATOR_OBJ
          );
        }
        
        distributionPlotOptions[key].chartType = 'violin';
        updated = true;
      }
    }
    
    if (updated) {
      onDistributionPlotOptions(Object.assign({}, distributionPlotOptions));
    }
  }, [distributionData, distributionPlotOptions, distributionPlotInterpolator, onDistributionPlotOptions]);

  const textColor = chartOptions.darkMode ? 'white' : 'black';
  const keys = Object.keys(distributionData);
  const typeToName = {
    X: 'Features',
    modules: 'Modules',
    obs: 'Observations',
  };
  
  // 按照与DistributionPlots相同的方式排序
  keys.sort((a, b) => {
    return a.toLowerCase() - b.toLowerCase();
  });
  const xIndex = keys.indexOf('X'); // 把features放在第一位
  if (xIndex !== -1) {
    keys.splice(xIndex, 1);
    keys.unshift('X');
  }

  return (
    <>
      {keys.map((key) => {
        const name = typeToName[key] || key;
        
        // 确保配置正确
        if (distributionPlotOptions[key] == null) {
          distributionPlotOptions[key] = Object.assign({}, DIST_PLOT_OPTIONS, {
            chartType: 'violin',
          });
          distributionPlotInterpolator[key] = Object.assign(
            {},
            DISTRIBUTION_PLOT_INTERPOLATOR_OBJ,
          );
        }
        
        // 始终确保图表类型为violin
        distributionPlotOptions[key].chartType = 'violin';
        
        let dimension2data = groupBy(distributionData[key], 'dimension');
        let dimension2selecteddata = groupBy(
          selectedDistributionData[key],
          'dimension',
        );
        
        return (
          <div key={key}>
            <Typography color="textPrimary" variant={'h5'}>
              {name}
            </Typography>
            {Object.keys(dimension2data).map((dimension) => {
              const data = dimension2data[dimension];
              const categoryColorScales = [];
              
              // 获取每个维度的颜色比例尺
              data[0].dimensions.forEach((dimension) => {
                let found = false;
                for (let i = 0; i < embeddingData.length; i++) {
                  if (dimension === embeddingData[i].name) {
                    categoryColorScales.push(embeddingData[i].colorScale);
                    found = true;
                    break;
                  }
                }
                if (!found) {
                  categoryColorScales.push(scaleOrdinal(schemeCategory10));
                }
              });

              return (
                <DistributionGroup
                  key={dimension}
                  cachedData={cachedData}
                  showDotPlotOption={false} // 禁用点图选项
                  categoryColorScales={categoryColorScales}
                  dataset={dataset}
                  distributionData={data}
                  globalFeatureSummary={globalFeatureSummary}
                  selectedData={dimension2selecteddata[dimension]}
                  interpolator={distributionPlotInterpolator[key]}
                  distributionPlotOptions={{
                    ...distributionPlotOptions[key],
                    chartType: 'violin' // 强制使用热图
                  }}
                  categoricalNames={categoricalNames}
                  textColor={textColor}
                  handleInterpolator={partial(onInterpolator, key)}
                  onColorScalingChange={partial(onColorScalingChange, key)}
                  onDistributionPlotOptions={partial(
                    onDistributionPlotOptionsDataType,
                    key,
                  )}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    cachedData: state.cachedData,
    categoricalNames: state.categoricalNames,
    chartOptions: state.chartOptions,
    dataset: state.dataset,
    distributionData: state.distributionData,
    distributionPlotOptions: state.distributionPlotOptions,
    distributionPlotInterpolator: state.distributionPlotInterpolator,
    embeddingData: state.embeddingData,
    globalFeatureSummary: state.globalFeatureSummary,
    selectedDistributionData: state.selectedDistributionData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onDistributionPlotOptions: (payload) => {
      dispatch(setDistributionPlotOptions(payload));
    },
    onInterpolator: (value) => {
      dispatch(setDistributionPlotInterpolator(value));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ViolinDistributionPlot); 